import React, { useState, useEffect } from 'react';
import { 
    Layout, 
    Card, 
    Row, 
    Col, 
    Table, 
    Tag, 
    Button, 
    Space, 
    Select, 
    DatePicker, 
    message, 
    Typography 
} from 'antd';
import { 
    SearchOutlined, 
    ClearOutlined, 
    FileExcelOutlined, 
    ArrowLeftOutlined,
    FileSearchOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Content } = Layout;
const { RangePicker } = DatePicker;

const MonthlySalesDetailsReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [members, setMembers] = useState([]);
    const [filters, setFilters] = useState({
        member_id: '',
        statuses: [],
        dateRange: [null, null]
    });

    // Fetch members for dropdown
    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const response = await axiosClient.get('/member/get_all_members_dropdown.php');
                if (response.data.status === 'success') {
                    setMembers(response.data.data);
                }
            } catch (error) {
                console.error('Fetch members error:', error);
            }
        };
        fetchMembers();
    }, []);

    const columns = [
        {
            title: 'Invoice No',
            dataIndex: 'inv_ref',
            key: 'inv_ref',
            fixed: 'left',
            width: 120,
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Invoiced Date',
            dataIndex: 'process_dt',
            key: 'process_dt',
            width: 180,
            render: (date) => date ? dayjs(date).format('DD MMM YYYY hh:mm A') : 'N/A'
        },
        {
            title: 'Member ID',
            dataIndex: 'member_code',
            key: 'member_code',
            width: 120,
        },
        {
            title: 'Member Name',
            dataIndex: 'member_name',
            key: 'member_name',
            width: 200,
        },
        {
            title: 'Invoice Total',
            dataIndex: 'invoice_total',
            key: 'invoice_total',
            align: 'right',
            width: 130,
            render: (total) => `Rs. ${Number(total).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        },
        {
            title: 'Paid Amount',
            dataIndex: 'total_paid_amount',
            key: 'total_paid_amount',
            align: 'right',
            width: 130,
            render: (paid) => `Rs. ${Number(paid).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        },
        {
            title: 'Unpaid Amount',
            dataIndex: 'unpaid_amount',
            key: 'unpaid_amount',
            align: 'right',
            width: 130,
            render: (unpaid) => (
                <span style={{ color: unpaid > 0 ? '#f5222d' : '#52c41a', fontWeight: unpaid > 0 ? 'bold' : 'normal' }}>
                    Rs. {Number(unpaid).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
            )
        },
        {
            title: 'Status',
            dataIndex: 'inv_status',
            key: 'inv_status',
            width: 120,
            fixed: 'right',
            render: (status) => {
                let color = 'default';
                if (status === 'Completed') color = 'green';
                if (status === 'Pending') color = 'orange';
                if (status === 'Cancelled') color = 'red';
                return <Tag color={color}>{status.toUpperCase()}</Tag>;
            }
        }
    ];

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.member_id) params.append('member_id', filters.member_id);
            if (filters.statuses.length > 0) params.append('statuses', filters.statuses.join(','));
            
            if (filters.dateRange[0] && filters.dateRange[1]) {
                params.append('start_date', filters.dateRange[0].format('YYYY-MM-DD'));
                params.append('end_date', filters.dateRange[1].format('YYYY-MM-DD'));
            }

            const response = await axiosClient.get(`/reports/get_monthly_sales_details_report.php?${params.toString()}`);
            if (response.data.status === 'success') {
                setData(response.data.data || []);
                if (response.data.data.length === 0) {
                    message.info('No matching invoices found for the selected criteria.');
                }
            }
        } catch (error) {
            console.error('Fetch report error:', error);
            message.error('Failed to fetch sales details report.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFilters({
            member_id: '',
            statuses: [],
            dateRange: [null, null]
        });
        setData([]);
    };

    const exportToExcel = () => {
        if (data.length === 0) return;
        
        const worksheetData = data.map(item => ({
            'Invoice No': item.inv_ref,
            'Invoiced Date': item.process_dt ? dayjs(item.process_dt).format('DD MMM YYYY hh:mm A') : 'N/A',
            'Member ID': item.member_code,
            'Member Name': item.member_name,
            'Invoice Total': Number(item.invoice_total),
            'Paid Amount': Number(item.total_paid_amount),
            'Unpaid Amount': Number(item.unpaid_amount),
            'Status': item.inv_status
        }));

        const ws = XLSX.utils.json_to_sheet(worksheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales Details");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const finalData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(finalData, `Monthly_Sales_Details_${dayjs().format('YYYYMMDD')}.xlsx`);
    };

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Headerdiv />
            <Content style={{ padding: '24px' }}>
                <div style={{ marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                {/* Filter Card */}
                <Card 
                    title={
                        <Space>
                            <FileSearchOutlined />
                            <span>Monthly Sales Details Report</span>
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />} 
                                onClick={fetchReport} 
                                loading={loading}
                            >
                                Generate
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClear}>Clear</Button>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Select Member</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="All Members"
                                value={filters.member_id}
                                onChange={(val) => setFilters({...filters, member_id: val})}
                                showSearch
                                optionFilterProp="children"
                                allowClear
                            >
                                <Select.Option value="">All Members</Select.Option>
                                {members.map(m => (
                                    <Select.Option key={m.id} value={m.id}>
                                        {m.member_id} - {m.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Invoice Status</div>
                            <Select 
                                mode="multiple"
                                style={{ width: '100%' }} 
                                placeholder="Select Statuses"
                                value={filters.statuses}
                                onChange={(val) => setFilters({...filters, statuses: val})}
                                maxTagCount="responsive"
                                allowClear
                            >
                                <Select.Option value="Completed">Completed</Select.Option>
                                <Select.Option value="Pending">Pending</Select.Option>
                                <Select.Option value="Cancelled">Cancelled</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Invoice Date Range</div>
                            <RangePicker 
                                style={{ width: '100%' }} 
                                value={filters.dateRange}
                                onChange={(dates) => setFilters({...filters, dateRange: dates})}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* Results Card */}
                <Card 
                    title={`Report Results ${data.length > 0 ? `(${data.length} records)` : ''}`}
                    extra={
                        data.length > 0 && (
                            <Button 
                                icon={<FileExcelOutlined />} 
                                onClick={exportToExcel}
                                style={{ backgroundColor: '#217346', color: '#fff', borderColor: '#217346' }}
                            >
                                Export to Excel
                            </Button>
                        )
                    }
                >
                    <Table 
                        dataSource={data}
                        columns={columns}
                        rowKey="inv_id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                        }}
                        bordered
                        size="small"
                        locale={{ emptyText: 'No data. Adjust filters and click Generate.' }}
                        scroll={{ x: 1300 }}
                    />
                </Card>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default MonthlySalesDetailsReport;
